import { infiniteQueryOptions, queryOptions } from '@tanstack/react-query';

import { api, unwrap } from '@/shared/api/client';
import type {
  CoinOrder,
  ConfirmCoinOrder,
  ConfirmedCoinOrder,
  CreateCoinOrder,
  CreatedCoinOrder,
  FailCoinOrder,
} from '@/shared/api/types';

/** 충전 내역 한 번에 불러오는 개수 */
const ORDER_PAGE_SIZE = 20;

export const coinKeys = {
  all: ['coins'] as const,
  packages: () => [...coinKeys.all, 'packages'] as const,
  orders: () => [...coinKeys.all, 'orders'] as const,
  orderList: () => [...coinKeys.orders(), 'list'] as const,
  order: (orderId: string) => [...coinKeys.orders(), 'detail', orderId] as const,
  confirm: (input: ConfirmCoinOrder) => [...coinKeys.all, 'confirm', input] as const,
  fail: (orderId: string, body: FailCoinOrder) => [...coinKeys.all, 'fail', orderId, body] as const,
};

/** 코인 패키지 목록 — 로그인 없이 볼 수 있고 자주 바뀌지 않는다 */
export const coinPackagesQueryOptions = () =>
  queryOptions({
    queryKey: coinKeys.packages(),
    queryFn: ({ signal }) => unwrap(api.GET('/api/coin-packages', { signal })),
    staleTime: 10 * 60_000,
  });

/** 주문 하나 — 결제 결과를 확인하는 동안 폴링한다 */
export const coinOrderQueryOptions = (orderId: string) =>
  queryOptions({
    queryKey: coinKeys.order(orderId),
    queryFn: ({ signal }): Promise<CoinOrder> =>
      unwrap(api.GET('/api/coin-orders/{orderId}', { params: { path: { orderId } }, signal })),
  });

/** 충전 내역 (최신순, 커서로 이어 읽기) */
export const coinOrdersInfiniteQueryOptions = () =>
  infiniteQueryOptions({
    queryKey: coinKeys.orderList(),
    queryFn: ({ pageParam, signal }) =>
      unwrap(
        api.GET('/api/coin-orders', {
          params: {
            query: { limit: ORDER_PAGE_SIZE, ...(pageParam ? { cursor: pageParam } : {}) },
          },
          signal,
        }),
      ),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: lastPage => lastPage.nextCursor ?? undefined,
  });

/**
 * 결제 승인 — 결제 성공 화면에서 결제 키마다 한 번만 보낸다.
 * 뮤테이션 대신 쿼리로 두어, StrictMode 의 effect 이중 실행·리렌더에도 같은 키의 요청이 하나로 합쳐진다
 * (같은 주문의 승인이 동시에 두 번 가면 하나는 '처리 중'으로 거절된다).
 * 서버는 같은 결제 키의 재요청(새로고침)에 현재 결과를 돌려준다. 실패는 다시 보내지 않는다.
 */
export const confirmCoinOrderQueryOptions = (input: ConfirmCoinOrder) =>
  queryOptions({
    queryKey: coinKeys.confirm(input),
    queryFn: (): Promise<ConfirmedCoinOrder> =>
      unwrap(api.POST('/api/coin-orders/confirm', { body: input })),
    staleTime: Infinity,
    gcTime: Infinity,
    retry: false,
  });

/** 결제창 실패 기록 — 결제 실패 화면에서 주문마다 한 번만 보낸다 (실패해도 화면에는 영향 없음) */
export const failCoinOrderQueryOptions = (orderId: string, body: FailCoinOrder) =>
  queryOptions({
    queryKey: coinKeys.fail(orderId, body),
    queryFn: (): Promise<CoinOrder> =>
      unwrap(api.POST('/api/coin-orders/{orderId}/fail', { params: { path: { orderId } }, body })),
    staleTime: Infinity,
    gcTime: Infinity,
    retry: false,
  });

export const coinOrdersApi = {
  /** 주문 생성 — 금액은 서버가 카탈로그로 정하고, 응답을 토스 결제창에 넘긴다 */
  create: (body: CreateCoinOrder): Promise<CreatedCoinOrder> =>
    unwrap(api.POST('/api/coin-orders', { body })),
};
